import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import { useAddEntityToSeason } from "~pages/SeasonEntities/SeasonEntityListPage/hooks/useAddEntityToSeason.ts";
import { useDeleteSeasonEntity } from "~pages/SeasonEntities/SeasonEntityListPage/hooks/useDeleteSeasonEntity.ts";
import { useExportSeasonEntities } from "~pages/SeasonEntities/SeasonEntityListPage/hooks/useExportSeasonEntities.ts";
import { useImportSeasonEntities } from "~pages/SeasonEntities/SeasonEntityListPage/hooks/useImportSeasonEntities.ts";
import type { UpsertSeasonEntityModel } from "~pages/SeasonEntities/types/upsertSeasonEntityModel.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import type { SeasonEntityResponse } from "~types/seasonEntity.ts";
import { ensureDefaultOperationResponse, isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useUpsertSeasonEntity } from "./useUpsertSeasonEntity.ts";

const isUpsertSucceeded = (response: SeasonEntityResponse) => Boolean(response?.data?.length);

export const useSeasonEntityService = () => {
  const { mutateAsync: upsertSeasonEntityFn, isPending: isUpsertPending } = useUpsertSeasonEntity();
  const { mutateAsync: addEntityToSeasonFn, isPending: isAddPending } = useAddEntityToSeason();
  const { mutateAsync: deleteSeasonEntityFn, isPending: isDeletePending } = useDeleteSeasonEntity();
  const { mutateAsync: importSeasonEntitiesFn, isPending: isImportPending } = useImportSeasonEntities();
  const { mutateAsync: exportSeasonEntitiesFn, isPending: isExportPending } = useExportSeasonEntities();

  const { create, update, remove, execute } = useApiOperations(ApiOperationItems.SEASON_ENTITY);

  const addEntityToSeason = async (entityId: string) => {
    const response = await create(() => addEntityToSeasonFn(entityId), {
      successMessageId: "seasonEntity.add.success",
      isSucceeded: isDefaultOperationSucceeded,
    });

    if (!response) {
      throw new Error("Season team was not added");
    }

    return response;
  };

  const upsertSeasonEntity = (payload: UpsertSeasonEntityModel) =>
    update(() => upsertSeasonEntityFn(payload), {
      successMessageId: "seasonEntity.update.success",
      isSucceeded: isUpsertSucceeded,
    });

  const deleteSeasonEntity = async (entityId: string) =>
    Boolean(
      await remove(() => deleteSeasonEntityFn(entityId), {
        successMessageId: "seasonEntity.delete.success",
        isSucceeded: isDefaultOperationSucceeded,
      }),
    );

  const importSeasonEntities = async (fileName: string) =>
    ensureDefaultOperationResponse(
      await execute(() => importSeasonEntitiesFn(fileName), {
        successMessageId: "seasonEntity.import.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Season teams import was not started",
    );

  const exportSeasonEntities = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    ensureDefaultOperationResponse(
      await execute(() => exportSeasonEntitiesFn(fieldNames), {
        successMessageId: "seasonEntity.export.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Season teams export was not started",
    );

  return {
    addEntityToSeason,
    isAddPending,
    upsertSeasonEntity,
    isUpsertPending,
    deleteSeasonEntity,
    isDeletePending,
    importSeasonEntities,
    isImportPending,
    exportSeasonEntities,
    isExportPending,
  };
};
